import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { Trophy, ScanLine, MessageCircle, Sparkles, ArrowRight, Users, Radio } from 'lucide-react';
import { tournamentsAPI } from '../lib/api';

/**
 * HomeLanding (route `/`)
 *
 * Hero landing for casual visitors and venue iPads. Two utilities live on this
 * page above the primary "Let's Begin" CTA:
 *
 *  1. Active Tournament Hub card — only when an in-progress/featured tournament
 *     exists. Shows a QR code pointing to the public spectator URL and a
 *     WhatsApp number capture box (reciprocity-framed copy).
 *
 *  2. Big glowing green "Let's Begin" button → /play (Quick Match scorer).
 *
 * Returning users who tap Let's Begin set a localStorage flag we don't use here
 * (the user explicitly wanted /  to always show landing first).
 */
export default function HomeLanding() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Fetch the tournament to feature (creator-pinned → newest in_progress → newest registration).
  // Re-fetch every 30s so the "LIVE" glow updates without a full page reload.
  useEffect(() => {
    let cancelled = false;
    const fetchFeatured = async () => {
      try {
        const res = await tournamentsAPI.getFeatured();
        if (!cancelled) setFeatured(res.data);
      } catch (err) {
        if (!cancelled) setFeatured(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFeatured();
    const id = setInterval(fetchFeatured, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const spectatorUrl = useMemo(() => {
    if (!featured) return '';
    return `${window.location.origin}/tournaments/${featured.tournament_id}`;
  }, [featured]);

  const handleSubscribe = async (e) => {
    e?.preventDefault?.();
    if (!featured) return;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) {
      toast.error('Enter a valid WhatsApp number');
      return;
    }
    setSubscribing(true);
    try {
      await tournamentsAPI.subscribe(featured.tournament_id, { phone });
      setSubscribed(true);
      toast.success("You're in! We'll send updates after each match.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not subscribe — try again');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="home-landing">
      {/* Stadium / court background image painted via CSS */}
      <div className="home-landing-stadium" aria-hidden />
      <div className="home-landing-veil" aria-hidden />

      <main className="home-landing-content">
        {/* Floating shuttlecocks — pure CSS, sit behind content */}
        <div className="home-shuttles" aria-hidden>
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className={`home-shuttle home-shuttle-${i + 1}`}>
              <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="9" r="3.6" fill="#fff" />
                <path d="M11.5 11.5 L7 25 L16 22 L25 25 L20.5 11.5 Z" fill="#fff" opacity="0.9" />
                <path d="M16 12 L16 22 M13.4 12.4 L11.8 23.4 M18.6 12.4 L20.2 23.4" stroke="rgba(40,90,140,0.45)" strokeWidth="0.7" strokeLinecap="round" />
              </svg>
            </span>
          ))}
        </div>

        {/* Hero image: stadium + LoveAll wordmark + shuttlecock + tagline + subtitle
            Single composite asset matches the canonical brand identity. */}
        <img
          src="/loveall-hero.png"
          alt="LoveAll — Your Ultimate Badminton Companion"
          className="home-landing-hero-img"
          data-testid="landing-hero-img"
        />

        {/* Active Tournament Hub — only when a featured tournament exists */}
        {loading ? (
          <div className="home-tournament-card home-tournament-card-skeleton" data-testid="featured-skeleton" />
        ) : featured ? (
          <div
            className={`home-tournament-card${featured.live_match_count > 0 ? ' is-live' : ''}`}
            data-testid="featured-tournament-card"
            data-live={featured.live_match_count > 0 ? 'true' : 'false'}
          >
            <div className="home-tournament-card-header">
              {featured.live_match_count > 0 ? (
                <>
                  <span className="home-live-dot" aria-hidden />
                  <span data-testid="featured-live-badge">
                    {featured.live_match_count === 1
                      ? '1 match live now'
                      : `${featured.live_match_count} matches live now`}
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                  <span>Active Tournament Hub</span>
                </>
              )}
            </div>

            <button
              type="button"
              className="home-tournament-name"
              onClick={() => navigate(`/tournaments/${featured.tournament_id}`)}
              data-testid="featured-tournament-name"
            >
              <Trophy className="h-5 w-5 text-amber-400 flex-shrink-0" />
              <span className="truncate">{featured.name}</span>
              <ArrowRight className="h-4 w-4 ml-auto opacity-60" />
            </button>

            <div className="home-tournament-meta">
              {featured.team_count > 0 && (
                <span className="home-tournament-meta-pill">
                  <Users className="h-3 w-3" /> {featured.team_count} {featured.team_count === 1 ? 'player' : 'players'}
                </span>
              )}
              <span className={`home-tournament-meta-pill home-tournament-meta-pill-${featured.status}`}>
                {featured.status === 'in_progress' ? 'Live now' : featured.status === 'registration' ? 'Open for entries' : featured.status}
              </span>
            </div>

            {/* QR + utility row */}
            <div className="home-qr-row">
              <div className="home-qr-block">
                <div className="home-qr-wrap" data-testid="featured-qr">
                  <QRCodeCanvas
                    value={spectatorUrl}
                    size={108}
                    bgColor="#ffffff"
                    fgColor="#0a0f1a"
                    level="M"
                    includeMargin
                  />
                </div>
                <div className="home-qr-label">
                  <ScanLine className="h-3.5 w-3.5" />
                  <span>Scan to follow</span>
                </div>
              </div>

              <div className="home-utility-block">
                <p className="home-utility-pitch">
                  Get your <strong>match progress, ELO updates &amp; brag cards</strong> sent straight to WhatsApp.
                </p>
                {subscribed ? (
                  <div className="home-utility-success" data-testid="subscribe-success">
                    <MessageCircle className="h-4 w-4 text-emerald-400" />
                    <span>You&apos;re subscribed. We&apos;ll buzz you after each match.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="home-utility-form">
                    <div className="home-utility-input-wrap">
                      <MessageCircle className="h-4 w-4 text-emerald-400 home-utility-icon" />
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="WhatsApp number (e.g. 9840xxxxxx)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="home-utility-input"
                        data-testid="subscribe-phone-input"
                        disabled={subscribing}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={subscribing || phone.replace(/\D/g, '').length < 8}
                      className="home-utility-submit"
                      data-testid="subscribe-submit"
                    >
                      {subscribing ? '…' : 'Send me updates'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Massive glowing CTA */}
        <button
          type="button"
          onClick={() => navigate('/play')}
          className="home-cta"
          data-testid="lets-begin-btn"
        >
          <svg className="home-cta-shuttle" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            {/* shuttlecock head */}
            <circle cx="16" cy="9" r="4" fill="currentColor" />
            {/* feather skirt */}
            <path d="M11 12 L7 26 L16 23 L25 26 L21 12 Z" fill="currentColor" opacity="0.94" />
            {/* feather creases */}
            <path d="M16 13 L16 23 M13 13.5 L11.5 24 M19 13.5 L20.5 24" stroke="rgba(10,60,20,0.5)" strokeWidth="0.9" strokeLinecap="round" />
          </svg>
          <span>Let&apos;s Begin</span>
        </button>

        <p className="home-cta-foot">
          Free · No login required · Score your first match in 10 seconds
        </p>
      </main>
    </div>
  );
}
